/**
 * useObstructionDetailsState
 *
 * Tracks the active section tab inside the obstruction details panel.
 * Mirrors `useTaskDetailsState` / `useNoteDetailState`:
 *
 * - Defaults to `'general'`.
 * - Resets to `'general'` whenever the selected obstruction changes.
 * - Falls back to `'general'` when the current tab is filtered out by
 *   availability (e.g. a tab was conditional on metadata that disappeared).
 */
import { useEffect, useState } from 'react';

export type ObstructionDetailTabType =
  | 'general'
  | 'contractor'
  | 'attachments'
  | 'history';

interface UseObstructionDetailsStateOptions {
  /** Available tab ids for the current obstruction (drives reset-on-disappear). */
  availableTabs: readonly ObstructionDetailTabType[];
}

interface UseObstructionDetailsStateResult {
  activeTab: ObstructionDetailTabType;
  setActiveTab: (tab: ObstructionDetailTabType) => void;
}

export function useObstructionDetailsState(
  obstructionId: string | undefined,
  { availableTabs }: UseObstructionDetailsStateOptions
): UseObstructionDetailsStateResult {
  const [activeTab, setActiveTab] =
    useState<ObstructionDetailTabType>('general');

  // Reset to 'general' on obstruction switch.
  useEffect(() => {
    setActiveTab('general');
  }, [obstructionId]);

  // Reset if the active tab is no longer available.
  useEffect(() => {
    if (!availableTabs.includes(activeTab)) {
      setActiveTab('general');
    }
  }, [activeTab, availableTabs]);

  return { activeTab, setActiveTab };
}
